import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import AssignmentLateRoundedIcon from '@mui/icons-material/AssignmentLateRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ExploreRoundedIcon from '@mui/icons-material/ExploreRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import ListItemButton from '@mui/material/ListItemButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

import axiosInstance from '../utils/axiosInstance';

const REFRESH_INTERVAL_MS = 60000;

const requestBucketLabels = {
  draft: 'Draft',
  awaiting_provider: 'Awaiting Provider',
  proposals_received: 'Proposals Received',
  in_progress: 'In Progress',
  completed: 'Completed',
  blocked: 'Blocked',
};

const requestBucketColors = {
  draft: 'default',
  awaiting_provider: 'warning',
  proposals_received: 'info',
  in_progress: 'primary',
  completed: 'success',
  blocked: 'error',
};

const assetCategoryConfig = {
  available: { label: 'Available', color: 'success', icon: <CheckCircleRoundedIcon /> },
  busy: { label: 'Busy', color: 'info', icon: <AutorenewRoundedIcon /> },
  faulted: { label: 'Faulted', color: 'error', icon: <ErrorRoundedIcon /> },
  offline: { label: 'Offline', color: 'warning', icon: <WarningAmberRoundedIcon /> },
};

function formatLabel(value) {
  return String(value || 'Unknown')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelativeTime(value) {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  const seconds = Math.round((parsed.getTime() - Date.now()) / 1000);
  const absoluteSeconds = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  if (absoluteSeconds < 60) return formatter.format(seconds, 'second');
  if (absoluteSeconds < 3600) return formatter.format(Math.round(seconds / 60), 'minute');
  if (absoluteSeconds < 86400) return formatter.format(Math.round(seconds / 3600), 'hour');
  if (absoluteSeconds < 2592000) return formatter.format(Math.round(seconds / 86400), 'day');
  return formatDateTime(value);
}

function statusColor(status) {
  const normalized = String(status || '').toLowerCase();
  if (['available', 'completed', 'connected', 'online', 'ready'].includes(normalized)) return 'success';
  if (['accepted', 'created', 'in_progress', 'running'].includes(normalized)) return 'primary';
  if (['open_for_proposals', 'proposal_submitted', 'queued', 'waiting'].includes(normalized)) return 'warning';
  if (['cancelled', 'declined', 'failed', 'faulted', 'offline'].includes(normalized)) return 'error';
  return 'default';
}

function activationProps(onOpen) {
  return {
    role: 'link',
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onOpen();
      }
    },
  };
}

function DashboardCard({ children, sx = {}, ...props }) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 3,
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.14),
        boxShadow: (theme) => `0 12px 32px ${alpha(theme.palette.common.black, 0.04)}`,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Card>
  );
}

function SectionHeader({ icon, title, count, actionLabel, actionTo, children }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            color: 'primary.main',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          }}
        >
          {icon}
        </Box>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {title}
            </Typography>
            {Number.isFinite(Number(count)) ? <Chip size="small" label={count} /> : null}
          </Stack>
          {children}
        </Box>
      </Stack>
      {actionLabel && actionTo ? (
        <Button component={RouterLink} to={actionTo} size="small" endIcon={<KeyboardArrowRightRoundedIcon />}>
          {actionLabel}
        </Button>
      ) : null}
    </Stack>
  );
}

function EmptyState({ title, description, success = false }) {
  return (
    <Stack alignItems="center" spacing={1} sx={{ py: 3, px: 2, textAlign: 'center' }}>
      {success ? <CheckCircleRoundedIcon color="success" /> : <SearchRoundedIcon color="disabled" />}
      <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  );
}

function DashboardSkeleton() {
  return (
    <Stack spacing={3}>
      <Skeleton variant="rounded" height={88} sx={{ borderRadius: 3 }} />
      <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3 }} />
      <Skeleton variant="rounded" height={330} sx={{ borderRadius: 3 }} />
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 7 }}><Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} /></Grid>
        <Grid size={{ xs: 12, lg: 5 }}><Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} /></Grid>
      </Grid>
    </Stack>
  );
}

function attentionIcon(kind) {
  if (kind === 'failed_run') return <ErrorRoundedIcon />;
  if (kind === 'asset_action') return <BuildRoundedIcon />;
  if (kind === 'need_opportunity') return <ExploreRoundedIcon />;
  if (kind === 'unclaimed_work') return <AssignmentLateRoundedIcon />;
  return <WarningAmberRoundedIcon />;
}

function AttentionRequired({ section, navigate }) {
  const items = section?.items || [];
  return (
    <DashboardCard
      sx={{
        borderColor: (theme) => alpha(theme.palette.warning.main, section?.count ? 0.38 : 0.16),
        background: (theme) => section?.count
          ? `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)}, ${alpha(theme.palette.background.paper, 0.98)} 48%)`
          : theme.palette.background.paper,
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <SectionHeader
          icon={<WarningAmberRoundedIcon />}
          title="Attention Required"
          count={section?.count || 0}
        >
          <Typography variant="body2" color="text.secondary">
            Work and operational events that may need your response.
          </Typography>
        </SectionHeader>
        <Divider sx={{ my: 2.5 }} />
        {!items.length ? (
          <EmptyState
            success
            title="Nothing needs your attention"
            description="Your requests, workstreams, runs, and assets have no immediate action items."
          />
        ) : (
          <Stack divider={<Divider flexItem />}>
            {items.map((item) => (
              <ListItemButton
                key={item.attention_id}
                onClick={() => navigate(item.target_url)}
                sx={{ px: 0.5, py: 1.5, borderRadius: 2, alignItems: 'flex-start' }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    color: `${item.severity || 'warning'}.main`,
                    bgcolor: (theme) => alpha(theme.palette[item.severity || 'warning']?.main || theme.palette.warning.main, 0.1),
                    mr: 1.5,
                    flex: '0 0 auto',
                  }}
                >
                  {attentionIcon(item.kind)}
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.detail}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: '0 0 auto' }}>
                      <Tooltip title={formatDateTime(item.occurred_at)}>
                        <Typography variant="caption" color="text.secondary">
                          {formatRelativeTime(item.occurred_at)}
                        </Typography>
                      </Tooltip>
                      <Button size="small" color={item.severity || 'primary'} endIcon={<OpenInNewRoundedIcon />}>
                        {item.action_label}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </ListItemButton>
            ))}
          </Stack>
        )}
      </CardContent>
    </DashboardCard>
  );
}

function WorkStatus({ item }) {
  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Chip size="small" color={statusColor(item.status)} label={formatLabel(item.status)} />
      {item.waiting_on ? (
        <Typography variant="caption" color="text.secondary">
          Waiting on {formatLabel(item.waiting_on)}
        </Typography>
      ) : null}
    </Stack>
  );
}

function WorkAssets({ assets = [] }) {
  if (!assets.length) return <Typography variant="body2" color="text.secondary">Not assigned</Typography>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {assets.slice(0, 2).map((asset) => <Chip key={asset.asset_id} size="small" variant="outlined" label={asset.asset_name} />)}
      {assets.length > 2 ? <Chip size="small" label={`+${assets.length - 2}`} /> : null}
    </Stack>
  );
}

function CurrentRun({ run, navigate }) {
  if (!run) return <Typography variant="body2" color="text.secondary">No active run</Typography>;
  return (
    <Button
      size="small"
      onClick={(event) => {
        event.stopPropagation();
        navigate(`/runs/${run.run_id}`);
      }}
      sx={{ px: 0.5, justifyContent: 'flex-start' }}
    >
      {run.asset?.asset_name || 'Run'} · {Math.round(Number(run.progress || 0))}%
    </Button>
  );
}

function ActiveWork({ section, navigate }) {
  const items = section?.items || [];
  return (
    <DashboardCard>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <SectionHeader
          icon={<AssignmentRoundedIcon />}
          title="Active Work"
          count={section?.count || 0}
          actionLabel="View All Work"
          actionTo="/my-work"
        >
          <Typography variant="body2" color="text.secondary">
            Current request fulfillment across your accessible AMX scope.
          </Typography>
        </SectionHeader>
      </CardContent>
      <Divider />
      {!items.length ? (
        <EmptyState title="No active work" description="Accepted and in-progress request workstreams will appear here." />
      ) : (
        <>
          <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Request / Work</TableCell>
                  <TableCell>Capability</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Assets</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Current Run</TableCell>
                  <TableCell>Last Activity</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.request_capability_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    {...activationProps(() => navigate(item.target_url))}
                  >
                    <TableCell sx={{ minWidth: 190 }}>
                      <Typography variant="body2" sx={{ fontWeight: 750 }}>{item.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.requesting_business_unit}</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>{item.capability_name}</TableCell>
                    <TableCell>{item.provider_business_unit}</TableCell>
                    <TableCell sx={{ minWidth: 150 }}><WorkAssets assets={item.assets} /></TableCell>
                    <TableCell><WorkStatus item={item} /></TableCell>
                    <TableCell sx={{ minWidth: 150 }}><CurrentRun run={item.current_run} navigate={navigate} /></TableCell>
                    <TableCell>
                      <Tooltip title={formatDateTime(item.updated_at)}>
                        <Typography variant="body2">{formatRelativeTime(item.updated_at)}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right"><OpenInNewRoundedIcon fontSize="small" color="action" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack divider={<Divider flexItem />} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {items.map((item) => (
              <ListItemButton key={item.request_capability_id} onClick={() => navigate(item.target_url)} sx={{ py: 2 }}>
                <Stack spacing={1.25} sx={{ width: '100%' }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.capability_name}</Typography>
                    </Box>
                    <WorkStatus item={item} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Provider: {item.provider_business_unit} · Updated {formatRelativeTime(item.updated_at)}
                  </Typography>
                  <WorkAssets assets={item.assets} />
                  <CurrentRun run={item.current_run} navigate={navigate} />
                </Stack>
              </ListItemButton>
            ))}
          </Stack>
        </>
      )}
    </DashboardCard>
  );
}

function ActiveRuns({ section, navigate }) {
  const items = section?.items || [];
  return (
    <DashboardCard>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <SectionHeader icon={<PrecisionManufacturingRoundedIcon />} title="Active Asset Runs" count={section?.count || 0} />
        <Divider sx={{ my: 2.5 }} />
        {!items.length ? (
          <EmptyState title="No active runs" description="Queued and running asset operations will appear here." />
        ) : (
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            {items.map((run) => {
              const progress = Number(run.progress);
              const hasProgress = Number.isFinite(progress);
              return (
                <Box
                  key={run.run_id}
                  sx={{ py: 0.5, cursor: 'pointer', borderRadius: 2 }}
                  {...activationProps(() => navigate(run.target_url))}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{run.asset?.asset_name || 'Asset Run'}</Typography>
                        <Typography variant="body2" color="text.secondary">{formatLabel(run.operation)}</Typography>
                      </Box>
                      <Chip size="small" color={statusColor(run.status)} label={formatLabel(run.status)} />
                    </Stack>
                    {hasProgress ? (
                      <Box>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">Progress</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 750 }}>{Math.round(progress)}%</Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, progress))} sx={{ mt: 0.5, borderRadius: 2 }} />
                      </Box>
                    ) : null}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 1 }} justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Started {formatRelativeTime(run.started_at)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Trigger: {run.trigger_source === 'amx' ? 'AMX / workflow' : formatLabel(run.trigger_source)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </DashboardCard>
  );
}

function MyRequests({ section, navigate }) {
  const items = section?.items || [];
  const counts = section?.counts || {};
  return (
    <DashboardCard>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <SectionHeader
          icon={<AssignmentRoundedIcon />}
          title="My Requests"
          count={section?.count || 0}
          actionLabel="View All Requests"
          actionTo="/requests"
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 1,
            my: 2.5,
          }}
        >
          {Object.entries(requestBucketLabels).map(([bucket, label]) => (
            <Paper key={bucket} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 850 }}>{counts[bucket] || 0}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Paper>
          ))}
        </Box>
        <Divider sx={{ mb: 1 }} />
        {!items.length ? (
          <EmptyState title="No requests yet" description="Create a request to begin capability fulfillment or provider discovery." />
        ) : (
          <Stack divider={<Divider flexItem />}>
            {items.map((request) => (
              <ListItemButton key={request.request_id} onClick={() => navigate(request.target_url)} sx={{ px: 0, py: 1.5 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{request.title}</Typography>
                    {request.request_type === 'need_based' ? <Chip size="small" variant="outlined" label="Capability Search" /> : null}
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 0.75 }}>
                    <Chip
                      size="small"
                      color={requestBucketColors[request.status_bucket] || 'default'}
                      label={requestBucketLabels[request.status_bucket] || formatLabel(request.status)}
                    />
                    {request.proposal_count ? <Typography variant="caption" color="text.secondary">{request.proposal_count} proposal(s)</Typography> : null}
                    <Typography variant="caption" color="text.secondary">Updated {formatRelativeTime(request.updated_at)}</Typography>
                  </Stack>
                </Box>
                <KeyboardArrowRightRoundedIcon color="action" />
              </ListItemButton>
            ))}
          </Stack>
        )}
      </CardContent>
    </DashboardCard>
  );
}

function AssetsOverview({ section, navigate }) {
  const items = section?.items || [];
  const counts = section?.counts || {};
  return (
    <DashboardCard>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <SectionHeader
          icon={<Inventory2RoundedIcon />}
          title="Assets Overview"
          count={section?.count || 0}
          actionLabel="View All Assets"
          actionTo="/assets"
        />
        <Grid container spacing={1.25} sx={{ my: 2.5 }}>
          {Object.entries(assetCategoryConfig).map(([category, config]) => (
            <Grid key={category} size={{ xs: 6, sm: 3 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  height: '100%',
                  color: `${config.color}.main`,
                  bgcolor: (theme) => alpha(theme.palette[config.color].main, 0.06),
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {config.icon}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 850, lineHeight: 1 }}>{counts[category] || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">{config.label}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
        {counts.unknown ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {counts.unknown} asset(s) do not currently report enough status data for classification.
          </Typography>
        ) : null}
        <Divider sx={{ mb: 1 }} />
        {!items.length ? (
          <EmptyState title="No notable asset activity" description="Running, faulted, offline, and action-required assets will appear here." />
        ) : (
          <Stack divider={<Divider flexItem />}>
            {items.map((asset) => (
              <ListItemButton key={asset.asset_id} onClick={() => navigate(asset.target_url)} sx={{ px: 0, py: 1.4 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{asset.asset_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{asset.business_unit}</Typography>
                    </Box>
                    <Chip size="small" color={statusColor(asset.category)} label={formatLabel(asset.category)} />
                  </Stack>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                    {asset.operator_required ? <Chip size="small" color="error" variant="outlined" label="Operator Required" /> : null}
                    {asset.maintenance_required ? <Chip size="small" color="warning" variant="outlined" label="Maintenance" /> : null}
                    {asset.current_run_id ? <Chip size="small" variant="outlined" label={`Run ${Math.round(Number(asset.current_run_progress || 0))}%`} /> : null}
                  </Stack>
                </Box>
                <KeyboardArrowRightRoundedIcon color="action" />
              </ListItemButton>
            ))}
          </Stack>
        )}
      </CardContent>
    </DashboardCard>
  );
}

function activityIcon(item) {
  if (item.type?.startsWith('run.')) return <PrecisionManufacturingRoundedIcon fontSize="small" />;
  if (item.type?.startsWith('proposal.')) return <ExploreRoundedIcon fontSize="small" />;
  if (item.type === 'asset.offline') return <WarningAmberRoundedIcon fontSize="small" />;
  return <HistoryRoundedIcon fontSize="small" />;
}

function RecentActivity({ section, navigate }) {
  const items = section?.items || [];
  return (
    <DashboardCard>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <SectionHeader icon={<HistoryRoundedIcon />} title="Recent Activity" />
        <Divider sx={{ my: 2.5 }} />
        {!items.length ? (
          <EmptyState title="No recent activity" description="Request, workstream, run, proposal, and asset events will appear here." />
        ) : (
          <Stack spacing={0}>
            {items.map((item, index) => (
              <Box
                key={item.activity_id}
                sx={{ display: 'grid', gridTemplateColumns: '34px minmax(0, 1fr)', columnGap: 1.25, cursor: 'pointer' }}
                {...activationProps(() => navigate(item.target_url))}
              >
                <Stack alignItems="center" sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      color: `${item.severity || 'primary'}.main`,
                      bgcolor: (theme) => alpha(theme.palette[item.severity || 'primary']?.main || theme.palette.primary.main, 0.1),
                    }}
                  >
                    {activityIcon(item)}
                  </Box>
                  {index < items.length - 1 ? <Box sx={{ width: 1, flexGrow: 1, bgcolor: 'divider', my: 0.5 }} /> : null}
                </Stack>
                <Box sx={{ pb: index < items.length - 1 ? 2 : 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>{item.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{item.detail}</Typography>
                  <Tooltip title={formatDateTime(item.timestamp)}>
                    <Typography variant="caption" color="text.secondary">
                      {item.actor ? `${item.actor} · ` : ''}{formatRelativeTime(item.timestamp)}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </DashboardCard>
  );
}

export default function MainGrid() {
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState('');
  const [state, setState] = useState({
    data: null,
    loading: true,
    refreshing: false,
    error: '',
  });

  const loadDashboard = useCallback(async ({ background = false } = {}) => {
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    setState((current) => ({
      ...current,
      loading: !background && !current.data,
      refreshing: background || Boolean(current.data),
      error: '',
    }));
    try {
      const response = await axiosInstance.get('/dashboard/home', {
        params: selectedBusinessUnitId ? { business_unit_id: selectedBusinessUnitId } : {},
        signal: abortController.signal,
      });
      setState({ data: response.data, loading: false, refreshing: false, error: '' });
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || abortController.signal.aborted) return;
      setState((current) => ({
        ...current,
        loading: false,
        refreshing: false,
        error: error?.response?.data?.message || error?.message || 'Unable to load the AMX dashboard.',
      }));
    }
  }, [selectedBusinessUnitId]);

  useEffect(() => {
    loadDashboard();
    const refreshTimer = window.setInterval(() => loadDashboard({ background: true }), REFRESH_INTERVAL_MS);
    const refreshOnFocus = () => loadDashboard({ background: true });
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      window.clearInterval(refreshTimer);
      window.removeEventListener('focus', refreshOnFocus);
      abortControllerRef.current?.abort();
    };
  }, [loadDashboard]);

  const data = state.data;
  const scopeOptions = data?.scope?.business_units || [];
  const lastUpdated = useMemo(() => formatRelativeTime(data?.generated_at), [data?.generated_at]);

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          {scopeOptions.length > 1 ? (
            <FormControl size="small" sx={{ minWidth: 230 }}>
              <InputLabel id="home-dashboard-business-unit-label">Business Unit</InputLabel>
              <Select
                labelId="home-dashboard-business-unit-label"
                label="Business Unit"
                value={selectedBusinessUnitId}
                onChange={(event) => setSelectedBusinessUnitId(event.target.value)}
              >
                <MenuItem value="">All Accessible Business Units</MenuItem>
                {scopeOptions.map((businessUnit) => (
                  <MenuItem key={businessUnit.business_unit_id} value={businessUnit.business_unit_id}>
                    {businessUnit.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : data?.scope?.label ? (
            <Chip label={`Business Unit: ${data.scope.label}`} variant="outlined" />
          ) : null}
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ ml: { sm: 'auto' }, alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            {data?.generated_at ? (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Dashboard updated {lastUpdated}
              </Typography>
            ) : null}
            <Tooltip title={data?.generated_at ? `Updated ${formatDateTime(data.generated_at)}` : 'Refresh dashboard'}>
              <span>
                <IconButton onClick={() => loadDashboard({ background: true })} disabled={state.refreshing} aria-label="Refresh dashboard">
                  {state.refreshing ? <CircularProgress size={20} /> : <RefreshRoundedIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {state.error ? (
          <Alert
            severity={data ? 'warning' : 'error'}
            action={<Button color="inherit" size="small" onClick={() => loadDashboard()}>Retry</Button>}
          >
            {state.error}
          </Alert>
        ) : null}

        {state.loading && !data ? (
          <DashboardSkeleton />
        ) : data ? (
          <>
            <AttentionRequired section={data.attention_required} navigate={navigate} />
            <ActiveWork section={data.active_work} navigate={navigate} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 7 }}><ActiveRuns section={data.active_runs} navigate={navigate} /></Grid>
              <Grid size={{ xs: 12, lg: 5 }}><MyRequests section={data.my_requests} navigate={navigate} /></Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 7 }}><AssetsOverview section={data.assets_overview} navigate={navigate} /></Grid>
              <Grid size={{ xs: 12, lg: 5 }}><RecentActivity section={data.recent_activity} navigate={navigate} /></Grid>
            </Grid>
          </>
        ) : null}
      </Stack>
    </Box>
  );
}
